import TabsWithOutlet from '../../layouts/components/TabsWithOutlet';
import React from 'react';
import { Navigate, Route, Routes, useParams, useNavigate } from 'react-router-dom';
import { DataManagementTab } from '@shared/components/data-management';
import { useConfirmation } from '@shared/components/confirmation';
import { useRemoveUniversityAdmin } from './hooks/useUniversityAdmins';
import { createDomainsConfig } from './config/domainsConfig';
import { createUniversityAdminsConfig } from './config/universityAdminsConfig';
import { createTenantUsersConfig } from './config/tenantUsersConfig';
import { TenantRouteParams } from './types';
import { Box, Typography, Container, Alert } from '@mui/material';
import { useBreadcrumbManager } from '../../hooks/useBreadcrumbManager';
import { DomainRoutes } from '../domain';
import { createSurveyTemplatesConfig } from '../survey-template';
import { TemplateBuilder } from '../survey-template/pages/TemplateBuilder';
import { useAllTenantReportsConfig } from '../survey-report/config/allTenantReportsConfig';
import { useAuthStore } from '../authentication';
import { UserRole } from '../authentication/types';
import ReportViewPage from '../reports/pages/ReportViewPage';
import { useContextStore } from '../../stores/contextStore';
import { useHasRole } from '../../hooks/useActiveRole';

// Institutes component that uses the config with navigation
const InstitutesTab: React.FC = () => {
  const { tenantId } = useParams<TenantRouteParams>();
  const navigate = useNavigate();
  const domainsConfig = createDomainsConfig(tenantId!, navigate);

  return <DataManagementTab config={domainsConfig} />;
};

// University admins component using the enhanced config
const UniversityAdminsTab: React.FC = () => {
  const { tenantId } = useParams<TenantRouteParams>();
  const { confirm } = useConfirmation();
  const removeUniversityAdminMutation = useRemoveUniversityAdmin(tenantId!);
  const universityAdminsConfig = createUniversityAdminsConfig(
    tenantId!,
    removeUniversityAdminMutation,
    confirm
  );

  return <DataManagementTab config={universityAdminsConfig} />;
};

// Tenant users component (view-only)
const TenantUsersTab: React.FC = () => {
  const { tenantId } = useParams<TenantRouteParams>();
  const tenantUsersConfig = createTenantUsersConfig(tenantId!);

  return <DataManagementTab config={tenantUsersConfig} />;
};

// Survey Templates component
const SurveyTemplatesTab: React.FC = () => {
  const { tenantId } = useParams<TenantRouteParams>();
  const navigate = useNavigate();
  const surveyTemplatesConfig = createSurveyTemplatesConfig(tenantId!, navigate);

  return <DataManagementTab config={surveyTemplatesConfig} />;
};

// Reports component - tenant-specific reports view
const ReportsTab: React.FC = () => {
  const { tenantId: urlTenantId } = useParams<TenantRouteParams>();
  const { currentTenant } = useAuthStore();
  const { setActiveTenantId } = useContextStore();
  const navigate = useNavigate();

  // System admins can access any tenant, university admins are restricted to their own tenant
  const isSystemAdmin = useHasRole(UserRole.SYSTEM_ADMIN);
  const actualTenantId = isSystemAdmin ? urlTenantId : currentTenant?.id;

  // Create the config for all tenant reports (must be called before any early returns)
  const reportsConfig = useAllTenantReportsConfig(actualTenantId ?? '', navigate);

  // For system admins, set the context to match the tenant they're viewing
  React.useEffect(() => {
    if (isSystemAdmin && urlTenantId && urlTenantId !== currentTenant?.id) {
      setActiveTenantId(urlTenantId);
    }
  }, [isSystemAdmin, urlTenantId, currentTenant?.id, setActiveTenantId]);

  if (!actualTenantId) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="warning">
          No tenant context available. Please ensure you are accessing this page with a valid tenant
          context.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Survey Reports
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          View all survey reports across all subjects in this university. This comprehensive view
          allows you to monitor and manage reports from all departments and institutes.
        </Typography>

        <DataManagementTab config={reportsConfig} />
      </Box>
    </Container>
  );
};

// Main tenant routes component
export const TenantRoutes: React.FC = () => {
  const { tenantId } = useParams<TenantRouteParams>();

  // Initialize breadcrumb manager to automatically fetch and set tenant names
  useBreadcrumbManager();

  // Get auth store values and context store for system admin context management
  const { currentTenant } = useAuthStore();
  const { setActiveTenantId } = useContextStore();

  // System admins can access any tenant, university admins are restricted to their own tenant
  const isSystemAdmin = useHasRole(UserRole.SYSTEM_ADMIN);

  // For system admins, set the context to match the tenant they're viewing
  React.useEffect(() => {
    if (isSystemAdmin && tenantId && tenantId !== currentTenant?.id) {
      setActiveTenantId(tenantId);
    }
  }, [isSystemAdmin, tenantId, currentTenant?.id, setActiveTenantId]);

  // Validate tenantId parameter
  if (!tenantId) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Invalid university ID
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Please select a valid university from the list.
        </Typography>
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <TabsWithOutlet
            tabs={[
              { label: 'Institutes', href: `/app/manage/universities/${tenantId}/institutes` },
              {
                label: 'University Admins',
                href: `/app/manage/universities/${tenantId}/university-admins`,
              },
              { label: 'Users', href: `/app/manage/universities/${tenantId}/users` },
              {
                label: 'Survey Templates',
                href: `/app/manage/universities/${tenantId}/survey-templates`,
              },
              { label: 'Reports', href: `/app/manage/universities/${tenantId}/reports` },
            ]}
          />
        }
      >
        <Route
          index
          element={<Navigate to={`/app/manage/universities/${tenantId}/institutes`} replace />}
        />
        <Route path="institutes" element={<InstitutesTab />} />
        <Route path="university-admins" element={<UniversityAdminsTab />} />
        <Route path="users" element={<TenantUsersTab />} />
        <Route path="survey-templates" element={<SurveyTemplatesTab />} />
        <Route path="reports" element={<ReportsTab />} />
      </Route>

      {/* Nested domain routes */}
      <Route path="institutes/:domainId/*" element={<DomainRoutes />} />

      {/* Survey Template Builder Routes */}
      <Route path="survey-templates/:templateId/builder" element={<TemplateBuilder />} />

      {/* Report view route */}
      <Route path="reports/:reportId/view" element={<ReportViewPage />} />
    </Routes>
  );
};
